/* eslint-disable react/no-unstable-nested-components */
import { composeChannelFilters } from '@features/components/Chat/filters/composeChannelFilters';
import React, { useCallback, useMemo, useState } from 'react';
import { VscDiscard } from 'react-icons/vsc';
import { ChannelSort, Channel } from 'stream-chat';
import { ChannelList, ComponentProvider } from 'stream-chat-react';
import type { SearchResultItemProps, SearchResultsListProps } from 'stream-chat-react';
import { renderFilteredChannels } from '@features/components/Chat/utils';
import { useStreamChatContext } from '@features/contexts/ChatProvider';
import { CustomEmptyMessagesState } from '@features/components/Chat/components/ChatContent/Messages/EmptyComponents/CustomEmptyMessagesState';
import { ChannelFilters } from '@features/components/Chat/types/streamChatFilters';
import { ChatFilterState } from '@features/components/Chat/types';
import { CHAT_LABEL_SEARCH_PLACEHOLDER } from '@features/components/Chat/constants';
import { SearchEmpty } from '@features/components/Chat/components/ChatContent/EmptyComponents/SearchEmpty';
import { SearchLoading } from '@features/components/Chat/components/ChatContent/LoadingStates/SearchLoading';
import SearchResultItem from '@features/components/Chat/components/ChatContent/CustomSearchResultItem';
import { useChannelSearch } from '../hooks/useChannelSearch';
import { CustomChannelPreview } from './CustomChannelPreview';
import { SearchResultsList } from './CustomSearchResultsList';

export type ChatChannelListProps = {
  streamChatFilters?: ChannelFilters;
  sortOption: ChannelSort;
  caseId?: string;
  uiFilters: ChatFilterState;
  inlineLayout: boolean;
  onChannelSelect?: (channel: Channel) => void;
};

export const ChatChannelList = ({
  streamChatFilters,
  sortOption,
  caseId,
  uiFilters,
  inlineLayout,
  onChannelSelect,
}: ChatChannelListProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { chatTypes } = useStreamChatContext();

  const filters = useMemo(() => {
    return composeChannelFilters({
      base: streamChatFilters ?? {},
      ui: uiFilters,
    });
  }, [streamChatFilters, uiFilters]);

  const { searchFunction, hasMore, isLoadingMore, loadMore } = useChannelSearch({
    filters,
    sortOption,
    onSearch: setSearchQuery,
  });

  const CustomSearchResultItem = useCallback(
    (props: SearchResultItemProps) => <SearchResultItem {...props} searchQuery={searchQuery} />,
    [searchQuery],
  );

  const CustomSearchResultsList = useCallback(
    (props: SearchResultsListProps) => (
      <SearchResultsList {...props} hasMore={hasMore} isLoadingMore={isLoadingMore} onLoadMore={loadMore} />
    ),
    [hasMore, isLoadingMore, loadMore],
  );

  const Preview = useCallback(
    (props: React.ComponentProps<typeof CustomChannelPreview>) => (
      <CustomChannelPreview {...props} onChannelSelect={onChannelSelect} />
    ),
    [onChannelSelect],
  );

  return (
    <ComponentProvider value={{ ChannelPreviewActionButtons: () => null }}>
      <ChannelList
        filters={filters}
        sort={sortOption}
        options={{ limit: 10 }}
        channelRenderFilterFn={(channels) =>
          renderFilteredChannels(channels, { caseId, uiFilters, inlineLayout, chatTypes })
        }
        setActiveChannelOnMount={false}
        showChannelSearch
        additionalChannelSearchProps={{
          searchForChannels: true,
          clearSearchOnClickOutside: false,
          placeholder: CHAT_LABEL_SEARCH_PLACEHOLDER,
          searchFunction,
          SearchEmpty,
          SearchLoading,
          SearchResultItem: CustomSearchResultItem,
          SearchResultsList: CustomSearchResultsList,
          ExitSearchIcon: VscDiscard,
        }}
        Preview={Preview}
        EmptyStateIndicator={CustomEmptyMessagesState}
      />
    </ComponentProvider>
  );
};
